import axios, { AxiosResponse } from 'axios';
import {
  BigTimeApiResponse,
  BigTimeReportRequest,
  BigTimeRawMatterArray,
  Matter,
  BigTimeConfig,
  AppError,
} from '../types';
import { transformBigTimeData } from '../utils/dataTransformers';
import { formatDate } from '../utils/dateUtils';
import { getApiToken } from './authToken';

class BigTimeService {
  private config: BigTimeConfig;
  private axiosInstance;

  constructor(config: BigTimeConfig) {
    this.config = config;
    this.axiosInstance = axios.create({
      baseURL: config.baseUrl,
      timeout: config.timeoutMs,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Attach the signed-in user's token to every call. The backend rejects
    // any request to /api/bigtime that does not carry a valid one.
    this.axiosInstance.interceptors.request.use(
      async config => {
        // eslint-disable-next-line no-console
        console.log(`BigTime API Request: ${config.method?.toUpperCase()} ${config.url}`);
        const token = await getApiToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      error => {
        // eslint-disable-next-line no-console
        console.error('BigTime API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      response => {
        // eslint-disable-next-line no-console
        console.log(`BigTime API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      error => {
        // eslint-disable-next-line no-console
        console.error('BigTime API Response Error:', error);

        // Note: Authentication is now handled by MSAL in the React frontend
        // 401 errors should be rare since the frontend requires authentication

        const appError: AppError = {
          code: error.response?.status || 'NETWORK_ERROR',
          message: error.response?.data?.message || error.message || 'Unknown error occurred',
          details: error.response?.data,
          timestamp: new Date(),
        };
        return Promise.reject(appError);
      }
    );
  }

  /**
   * Get report metadata to understand field structure
   */
  async getReportMetadata(): Promise<any> {
    try {
      const response: AxiosResponse<any> =
        await this.axiosInstance.get(`/report/detail/${this.config.reportId}`);

      console.log('BigTime Report Metadata:', response.data);

      // Log column information to help with field mapping
      if (response.data.Columns) {
        console.log('Available columns:');
        response.data.Columns.forEach((col: any, index: number) => {
          console.log(`  [${index}] ${col.Id}: ${col.Name} (${col.FieldType})`);
        });
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching report metadata:', error);
      throw error;
    }
  }

  /**
   * Fetch calendar data for a specific date range
   */
  async getCalendarData(startDate: Date, endDate: Date): Promise<Matter[]> {
    try {
      const request = {
        DT_BEGIN: formatDate(startDate, 'yyyy-MM-dd'),
        DT_END: formatDate(endDate, 'yyyy-MM-dd'),
      };

      const response: AxiosResponse<BigTimeApiResponse> =
        await this.axiosInstance.post(`/report/data/${this.config.reportId}`, request);

      if (!response.data || !response.data.Data || response.status !== 200) {
        throw new Error('Failed to fetch calendar data');
      }

      // Log raw data structure to understand field mapping
      if (response.data.Data.length > 0) {
        console.log('BigTime Raw Data Sample:', response.data.Data[0]);
        console.log('Total data length:', response.data.Data.length);
      }

      return transformBigTimeData(response.data.Data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      throw error;
    }
  }

  /**
   * Get a specific matter by ID
   */
  async getMatterById(matterId: string): Promise<Matter | null> {
    try {
      const response: AxiosResponse<BigTimeApiResponse> =
        await this.axiosInstance.get(`/project/${matterId}`);

      if (!response.data || !response.data.Data || !response.data.Data.length) {
        return null;
      }

      const transformed = transformBigTimeData(response.data.Data);
      return transformed[0] || null;
    } catch (error) {
      console.error(`Error fetching matter ${matterId}:`, error);
      throw error;
    }
  }

  /**
   * Get all available consultants
   */
  async getConsultants(): Promise<string[]> {
    try {
      const response: AxiosResponse<{ Name: string }[]> =
        await this.axiosInstance.get('/consultants');

      if (!response.data) {
        throw new Error('Failed to fetch consultants');
      }

      return response.data.map(consultant => consultant.Name);
    } catch (error) {
      console.error('Error fetching consultants:', error);
      throw error;
    }
  }

  /**
   * Get all available clients
   */
  async getClients(): Promise<string[]> {
    try {
      const response: AxiosResponse<{ Name: string }[]> =
        await this.axiosInstance.get('/clients');

      if (!response.data) {
        throw new Error('Failed to fetch clients');
      }

      return response.data.map(client => client.Name);
    } catch (error) {
      console.error('Error fetching clients:', error);
      throw error;
    }
  }

  /**
   * Update matter notes
   */
  async updateMatterNotes(matterId: string, notes: string): Promise<boolean> {
    try {
      const response: AxiosResponse<any> = await this.axiosInstance.put(
        `/project/${matterId}/notes`,
        { notes }
      );

      return response.status === 200;
    } catch (error) {
      console.error(`Error updating matter ${matterId} notes:`, error);
      throw error;
    }
  }

  /**
   * Test API connection
   */
  async testConnection(): Promise<boolean> {
    try {
      const response: AxiosResponse<any> = await this.axiosInstance.get('/staff');
      return response.status === 200;
    } catch (error) {
      console.error('BigTime API connection test failed:', error);
      return false;
    }
  }

  /**
   * Generate BigTime deep link URL
   */
  generateDeepLink(matterId: string): string {
    return `https://iq.bigtime.net/Bigtime/Project2/soloview#/detail/${matterId}`;
  }

  /**
   * Refresh API token (manual process with BigTime)
   */
  async refreshToken(): Promise<string> {
    throw new Error(
      'Token refresh not supported. Please re-authenticate manually with BigTime API.'
    );
  }
}

// Create and export a singleton instance
const bigtimeConfig: BigTimeConfig = {
  baseUrl: process.env.REACT_APP_BIGTIME_API_URL || '/api/bigtime',
  apiToken: '', // Now handled server-side
  firmId: '', // Now handled server-side
  reportId: process.env.REACT_APP_BIGTIME_REPORT_ID || '315875',
  timeoutMs: 30000,
};

export const bigtimeService = new BigTimeService(bigtimeConfig);
export default BigTimeService;
